import { useCallback, useEffect, useState } from "react";
import {
    Loader2, Send, ArrowRightCircle, CheckCircle2, Undo2, History as HistoryIcon,
    AlertTriangle, Save, Clock, XCircle, UserX, Trash2, Pencil,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import EvidenceSection from "@/components/cctv/EvidenceSection";
import { CCTV } from "@/constants/testIds";

const STATUS_VARIANT = {
    draft: "secondary", submitted: "default", with_ops: "warning",
    ops_approved: "success", returned_to_monitor: "destructive",
};

const SEV_VARIANT = {
    low: "secondary", medium: "warning", high: "brown", critical: "destructive",
};

function dayName(iso, lang) {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", { weekday: "long" });
}

// HR escalation status shown on an approved finding. Mirrors the QC violations
// HR-push badge so operators see queued/sent/failed with the reason tooltip.
function HrPushRow({ finding, t }) {
    if (!finding || finding.status !== "ops_approved") return null;
    const hr = finding.hr_push;
    // Approved but no named employee → recorded finding, never escalated.
    if (!finding.employee_id) {
        return (
            <div data-testid={CCTV.hrPush} data-hr-push-status="none"
                className="flex items-center gap-2 rounded-lg border border-[color:var(--border-soft)] surface-2 p-2.5 text-xs text-[color:var(--ink-muted)]">
                <UserX className="h-4 w-4 shrink-0 text-[color:var(--ink-faint)]" />
                <span>{t("cctv_hr_push_none")}</span>
            </div>
        );
    }
    const MAP = {
        sent: { Icon: CheckCircle2, cls: "text-[color:var(--success)]", key: "cctv_hr_push_sent" },
        queued: { Icon: Clock, cls: "text-[color:var(--ink-muted)]", key: "cctv_hr_push_queued" },
        failed: { Icon: XCircle, cls: "text-[color:var(--danger)]", key: "cctv_hr_push_failed" },
    };
    const meta = MAP[hr?.status] || MAP.queued;
    const Icon = meta.Icon;
    return (
        <div data-testid={CCTV.hrPush} data-hr-push-status={hr?.status || "queued"}
            title={hr?.reason || hr?.last_error || ""}
            className="flex items-center gap-2 rounded-lg border border-[color:var(--border-soft)] surface-2 p-2.5 text-xs">
            <Send className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
            <span className="font-semibold text-[color:var(--ink)]">{t("cctv_hr_push_title")}:</span>
            <span className={`inline-flex items-center gap-1 font-medium ${meta.cls}`}>
                <Icon className="h-3.5 w-3.5" />{t(meta.key)}
                {hr?.status === "failed" && hr?.attempts ? <span className="tnum">({hr.attempts})</span> : null}
            </span>
        </div>
    );
}

const emptyForm = () => ({
    branch_id: "", incident_date: "", incident_time: "",
    category: "behavior", category_other: "", severity: "medium", description: "",
    employee_id: null, employee_name: null,
});

export default function CctvDetailDialog({ open, findingId, scope, onClose, onSaved }) {
    const { t, lang, tn } = useI18n();
    const { canDeleteEvidence } = useAuth();
    const [fid, setFid] = useState(null);
    const [detail, setDetail] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [editing, setEditing] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [empState, setEmpState] = useState({ available: null, reason: null });
    const [saving, setSaving] = useState(false);
    const [showReason, setShowReason] = useState(false);
    const [reason, setReason] = useState("");
    const [approveSeverity, setApproveSeverity] = useState("medium");
    const [showDelete, setShowDelete] = useState(false);

    const catDefaultSev = useCallback(
        (c) => (scope?.category_severity_default && scope.category_severity_default[c]) || "medium",
        [scope]
    );

    const loadDetail = useCallback(async (id) => {
        const r = await api.get(`/cctv/findings/${id}`);
        setDetail(r.data);
        // Natural edit (draft/returned) auto-opens in edit mode. Record-manager
        // override (can_edit on other statuses) opens in VIEW mode with an
        // explicit Edit button so approved findings aren't edited by accident.
        setEditing(!!r.data.can_edit && ["draft", "returned_to_monitor"].includes(r.data.status));
        setApproveSeverity(r.data.severity || "medium");
        setForm({
            branch_id: r.data.branch_id, incident_date: r.data.incident_date,
            incident_time: r.data.incident_time, category: r.data.category,
            category_other: r.data.category_other || "", severity: r.data.severity || "medium",
            description: r.data.description || "",
            employee_id: r.data.employee_id, employee_name: r.data.employee_name,
        });
        return r.data;
    }, []);

    useEffect(() => {
        if (!open) return;
        setShowReason(false); setReason(""); setShowDelete(false); setEmployees([]); setEmpState({ available: null, reason: null });
        if (findingId === "new") {
            setFid(null); setDetail(null); setForm(emptyForm()); setEditing(true);
        } else if (findingId) {
            setFid(findingId); loadDetail(findingId);
        }
    }, [open, findingId, loadDetail]);

    // load HR roster whenever the selected branch changes (edit mode only)
    useEffect(() => {
        if (!editing || !form.branch_id) { setEmployees([]); setEmpState({ available: null, reason: null }); return; }
        let alive = true;
        (async () => {
            setEmpState({ available: null, reason: "loading" });
            try {
                const r = await api.get("/cctv/employees", { params: { branch_id: form.branch_id } });
                if (!alive) return;
                setEmployees(r.data.employees || []);
                setEmpState({ available: r.data.available, reason: r.data.reason });
            } catch (e) {
                if (alive) setEmpState({ available: false, reason: "hr_unreachable" });
            }
        })();
        return () => { alive = false; };
    }, [editing, form.branch_id]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        if (!form.branch_id) { toast.error(t("cctv_f_branch")); return false; }
        if (!form.incident_date) { toast.error(t("cctv_f_date")); return false; }
        if (!form.incident_time) { toast.error(t("cctv_f_time")); return false; }
        if (form.category === "other" && !form.category_other.trim()) { toast.error(t("cctv_f_category_other")); return false; }
        if (!form.description.trim()) { toast.error(t("cctv_description_required")); return false; }
        return true;
    };

    const save = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body = {
                branch_id: form.branch_id, incident_date: form.incident_date, incident_time: form.incident_time,
                category: form.category, category_other: form.category === "other" ? form.category_other.trim() : null,
                severity: form.severity || "medium",
                description: form.description.trim(),
                employee_id: form.employee_id || null, employee_name: form.employee_name || null,
            };
            if (!fid) {
                const r = await api.post("/cctv/findings", body);
                setFid(r.data.id);
                await loadDetail(r.data.id);
            } else {
                await api.patch(`/cctv/findings/${fid}`, { ...body, clear_employee: !form.employee_id });
                await loadDetail(fid);
            }
            toast.success(t("cctv_saved"));
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSaving(false);
        }
    };

    const doAction = async (path, payload) => {
        setSaving(true);
        try {
            await api.post(`/cctv/findings/${fid}/${path}`, payload || {});
            await loadDetail(fid);
            setShowReason(false); setReason("");
            toast.success(t("cctv_action_done"));
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSaving(false);
        }
    };

    const submitReject = () => {
        if (!reason.trim()) { toast.error(t("cctv_reason_required")); return; }
        doAction("reject", { reason: reason.trim() });
    };

    const deleteFinding = async () => {
        if (!fid) return;
        setSaving(true);
        try {
            await api.delete(`/cctv/findings/${fid}`);
            toast.success(t("cctv_deleted"));
            onSaved && onSaved();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSaving(false);
        }
    };

    const catLabel = (c) => t(`cctv_cat_${c}`);
    const sevLabel = (s) => t(`cctv_sev_${s}`);
    const EV_KEYS = { create: "cctv_ev_create", submit: "cctv_ev_submit", dispatch_to_ops: "cctv_ev_dispatch_to_ops", approve: "cctv_ev_approve", reject: "cctv_ev_reject", escalate_to_hr: "cctv_ev_escalate_to_hr", severity_change: "cctv_ev_severity_change", branch_change: "cctv_ev_branch_change" };
    const evLabel = (a) => (EV_KEYS[a] ? t(EV_KEYS[a]) : a);
    // a `branch_change` event carries both branches (names resolved by the server; the id when a branch is gone)
    const evBranch = (ev, side) => tn({ name_ar: ev[`${side}_branch_name_ar`], name_en: ev[`${side}_branch_name_en`] }) || ev[`${side}_branch_id`] || "—";
    // two rows can share a name (الشبوط and its finance-only ERP twin): then each side also shows its id
    const evBranchId = (ev, side) => (evBranch(ev, "from") === evBranch(ev, "to") ? String(ev[`${side}_branch_id`] || "").slice(0, 8) : "");

    const d = detail;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid={CCTV.dialog} className="max-w-2xl max-h-[92vh] overflow-y-auto scrollbar-thin">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {findingId === "new" ? t("cctv_new_finding") : t("cctv_detail")}
                        {d ? <Badge variant={STATUS_VARIANT[d.status]}>{t(`cctv_status_${d.status}`)}</Badge> : null}
                    </DialogTitle>
                </DialogHeader>

                {/* returned reason banner */}
                {d?.status === "returned_to_monitor" && d?.last_reason ? (
                    <div data-testid={CCTV.lastReason} className="flex items-start gap-2 rounded-lg border border-[color:var(--danger)] bg-[color:var(--danger-soft)] p-2.5 text-xs text-[color:var(--danger-soft-fg)]">
                        <Undo2 className="h-4 w-4 shrink-0" />
                        <span><b>{t("cctv_last_reason")}:</b> {d.last_reason}</span>
                    </div>
                ) : null}

                {/* HR escalation status (approved findings) */}
                <HrPushRow finding={d} t={t} />

                {/* fields */}
                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label>{t("cctv_f_branch")}</Label>
                        {editing ? (
                            <Select value={form.branch_id} onValueChange={(v) => { set("branch_id", v); set("employee_id", null); set("employee_name", null); }}>
                                <SelectTrigger data-testid={CCTV.fBranch}><SelectValue placeholder={t("cctv_f_branch")} /></SelectTrigger>
                                <SelectContent>
                                    {(scope?.branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{tn(b)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        ) : <p className="text-sm font-medium">{tn({ name_ar: d?.branch_name_ar, name_en: d?.branch_name_en })}</p>}
                    </div>

                    <div className="space-y-1.5">
                        <Label>{t("cctv_f_category")}</Label>
                        {editing ? (
                            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v, severity: catDefaultSev(v) }))}>
                                <SelectTrigger data-testid={CCTV.fCategory}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {(scope?.categories || []).map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        ) : <p className="text-sm font-medium">{catLabel(d?.category)}{d?.category === "other" && d?.category_other ? ` · ${d.category_other}` : ""}</p>}
                    </div>

                    <div className="space-y-1.5">
                        <Label>{t("cctv_f_severity")}</Label>
                        {editing ? (
                            <>
                                <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
                                    <SelectTrigger data-testid={CCTV.fSeverity}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(scope?.severities || ["low", "medium", "high", "critical"]).map((s) => <SelectItem key={s} value={s}>{sevLabel(s)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <p className="text-[11px] text-[color:var(--ink-faint)]">{t("cctv_severity_hint")}</p>
                            </>
                        ) : <Badge variant={SEV_VARIANT[d?.severity] || "secondary"}>{sevLabel(d?.severity || "medium")}</Badge>}
                    </div>

                    {editing && form.category === "other" ? (
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label>{t("cctv_f_category_other")}</Label>
                            <Input data-testid={CCTV.fCategoryOther} value={form.category_other} onChange={(e) => set("category_other", e.target.value)} placeholder={t("cctv_f_category_other_ph")} />
                        </div>
                    ) : null}

                    <div className="space-y-1.5">
                        <Label>{t("cctv_f_date")}</Label>
                        {editing ? (
                            <Input data-testid={CCTV.fDate} type="date" value={form.incident_date} onChange={(e) => set("incident_date", e.target.value)} />
                        ) : <p className="text-sm font-medium"><bdi dir="ltr">{d?.incident_date}</bdi></p>}
                        <p className="text-[11px] text-[color:var(--ink-faint)]">{dayName(editing ? form.incident_date : d?.incident_date, lang)}</p>
                    </div>

                    <div className="space-y-1.5">
                        <Label>{t("cctv_f_time")}</Label>
                        {editing ? (
                            <Input data-testid={CCTV.fTime} type="time" value={form.incident_time} onChange={(e) => set("incident_time", e.target.value)} />
                        ) : <p className="text-sm font-medium"><bdi dir="ltr">{d?.incident_time}</bdi></p>}
                    </div>

                    <div className="space-y-1.5 sm:col-span-2">
                        <Label>{t("cctv_f_employee")}</Label>
                        {editing ? (
                            <>
                                <Select
                                    value={form.employee_id || "__none__"}
                                    onValueChange={(v) => {
                                        if (v === "__none__") { set("employee_id", null); set("employee_name", null); }
                                        else { const emp = employees.find((x) => x.employee_id === v); set("employee_id", v); set("employee_name", emp?.full_name || null); }
                                    }}
                                    disabled={empState.available === false}
                                >
                                    <SelectTrigger data-testid={CCTV.fEmployee}><SelectValue placeholder={t("cctv_emp_pick")} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">{t("cctv_emp_none")}</SelectItem>
                                        {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name}{e.job_title ? ` · ${e.job_title}` : ""}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {empState.reason === "loading" ? <p className="text-[11px] text-[color:var(--ink-faint)]">{t("cctv_emp_loading")}</p> : null}
                                {empState.available === false ? <p className="text-[11px] text-[color:var(--brand-brown)]">{t(`cctv_emp_${empState.reason === "hr_unmapped" ? "unmapped" : empState.reason === "hr_unconfigured" ? "unconfigured" : "unreachable"}`)}</p> : null}
                            </>
                        ) : <p className="text-sm font-medium">{d?.employee_name || t("cctv_emp_none")}</p>}
                    </div>

                    <div className="space-y-1.5 sm:col-span-2">
                        <Label>{t("cctv_f_description")}</Label>
                        {editing ? (
                            <Textarea data-testid={CCTV.fDescription} rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("cctv_f_description_ph")} />
                        ) : <p className="text-sm whitespace-pre-wrap">{d?.description}</p>}
                    </div>
                </div>

                {/* evidence */}
                {fid ? (
                    <div className="pt-1 border-t border-[color:var(--border-soft)]">
                        <div className="pt-3">
                            <EvidenceSection
                                findingId={fid}
                                evidence={d?.evidence || []}
                                editable={editing}
                                canDelete={canDeleteEvidence}
                                onChanged={async () => { await loadDetail(fid); onSaved && onSaved(); }}
                                hrVideoMb={scope?.hr_video_mb || 150}
                                t={t}
                            />
                        </div>
                    </div>
                ) : (
                    <p className="text-xs italic text-[color:var(--ink-faint)]">{t("cctv_save_first")}</p>
                )}

                {/* history */}
                {d?.events?.length ? (
                    <div data-testid={CCTV.history} className="pt-3 border-t border-[color:var(--border-soft)]">
                        <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[color:var(--ink)]"><HistoryIcon className="h-4 w-4" /> {t("cctv_history")}</p>
                        <ol className="space-y-2">
                            {d.events.map((ev) => (
                                <li key={ev.id} className="flex items-start gap-2 text-xs">
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[color:var(--brand-orange)] shrink-0" />
                                    <div>
                                        <span className="font-semibold text-[color:var(--ink)]">{evLabel(ev.action)}</span>
                                        {ev.action === "severity_change" ? (
                                            <span className="text-[color:var(--brand-brown)]"> · {sevLabel(ev.from_status)} → {sevLabel(ev.to_status)}</span>
                                        ) : null}
                                        {ev.action === "branch_change" ? (
                                            <span data-testid={CCTV.historyBranchChange(ev.id)} className="text-[color:var(--brand-brown)]">
                                                <span> · </span>
                                                <span>{evBranch(ev, "from")}</span>
                                                {evBranchId(ev, "from") ? <span> (<bdi dir="ltr">{evBranchId(ev, "from")}</bdi>)</span> : null}
                                                <span> → </span>
                                                <span>{evBranch(ev, "to")}</span>
                                                {evBranchId(ev, "to") ? <span> (<bdi dir="ltr">{evBranchId(ev, "to")}</bdi>)</span> : null}
                                            </span>
                                        ) : null}
                                        <span className="text-[color:var(--ink-faint)]"> · {t("cctv_by")} {ev.actor_name} ({t(`role_${ev.actor_role}`)})</span>
                                        <span className="text-[color:var(--ink-faint)]"> · <bdi dir="ltr">{(ev.at || "").slice(0, 16).replace("T", " ")}</bdi></span>
                                        {ev.reason ? <div className="text-[color:var(--danger)]">{t("cctv_reason")}: {ev.reason}</div> : null}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                ) : null}

                {/* reject reason input */}
                {showReason ? (
                    <div className="space-y-2 rounded-lg border border-[color:var(--border-soft)] p-3">
                        <Label>{t("cctv_reject_reason")}</Label>
                        <Textarea data-testid={CCTV.rejectReason} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("cctv_reject_reason_ph")} />
                        <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => { setShowReason(false); setReason(""); }}>{t("cancel")}</Button>
                            <Button size="sm" variant="destructive" data-testid={CCTV.rejectConfirm} disabled={saving} onClick={submitReject}>{t("cctv_reject")}</Button>
                        </div>
                    </div>
                ) : null}

                {/* delete confirm */}
                {showDelete ? (
                    <div className="space-y-2 rounded-lg border border-[color:var(--danger)] bg-[color:var(--danger-soft)] p-3">
                        <p className="text-sm font-semibold text-[color:var(--danger-soft-fg)]">{t("cctv_delete_confirm_title")}</p>
                        <p className="text-xs text-[color:var(--danger-soft-fg)]">{t("cctv_delete_confirm_desc")}</p>
                        <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" data-testid={CCTV.deleteCancel} onClick={() => setShowDelete(false)} disabled={saving}>{t("cancel")}</Button>
                            <Button size="sm" variant="destructive" data-testid={CCTV.deleteConfirm} disabled={saving} onClick={deleteFinding}>{t("cctv_delete_ok")}</Button>
                        </div>
                    </div>
                ) : null}

                <DialogFooter className="flex-wrap gap-2">
                    {d?.can_edit && !editing ? (
                        <Button data-testid={CCTV.editBtn} variant="outline" onClick={() => setEditing(true)} disabled={saving} className="gap-1.5">
                            <Pencil className="h-4 w-4" />{t("cctv_edit_btn")}
                        </Button>
                    ) : null}
                    {editing ? (
                        <Button data-testid={CCTV.saveDraft} onClick={save} disabled={saving} className="gap-1.5">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{t("cctv_save_draft")}
                        </Button>
                    ) : null}
                    {d?.can_submit ? (
                        <Button data-testid={CCTV.submit} variant="default" onClick={() => doAction("submit")} disabled={saving} className="gap-1.5">
                            <Send className="h-4 w-4" />{t("cctv_submit")}
                        </Button>
                    ) : null}
                    {d?.can_dispatch ? (
                        <Button data-testid={CCTV.dispatch} onClick={() => doAction("dispatch")} disabled={saving || (d?.evidence_count || 0) < 1}
                            title={(d?.evidence_count || 0) < 1 ? t("cctv_evidence_required_dispatch") : ""} className="gap-1.5">
                            <ArrowRightCircle className="h-4 w-4" />{t("cctv_dispatch")}
                        </Button>
                    ) : null}
                    {d?.can_approve ? (
                        <div className="flex items-center gap-2">
                            <div className="inline-flex items-center gap-1.5">
                                <span className="text-[11px] text-[color:var(--ink-muted)] whitespace-nowrap">{t("cctv_approve_severity")}</span>
                                <Select value={approveSeverity} onValueChange={setApproveSeverity}>
                                    <SelectTrigger data-testid={CCTV.approveSeverity} className="h-9 w-28"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(scope?.severities || ["low", "medium", "high", "critical"]).map((s) => <SelectItem key={s} value={s}>{sevLabel(s)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button data-testid={CCTV.approve} onClick={() => doAction("approve", { severity: approveSeverity })} disabled={saving} className="gap-1.5 bg-[color:var(--success)] hover:opacity-90">
                                <CheckCircle2 className="h-4 w-4" />{t("cctv_approve")}
                            </Button>
                        </div>
                    ) : null}
                    {d?.can_reject ? (
                        <Button data-testid={CCTV.reject} variant="outline" onClick={() => setShowReason(true)} disabled={saving} className="gap-1.5">
                            <Undo2 className="h-4 w-4" />{t("cctv_reject")}
                        </Button>
                    ) : null}
                    {d?.can_delete ? (
                        <Button data-testid={CCTV.deleteBtn} variant="destructive" onClick={() => setShowDelete(true)} disabled={saving} className="gap-1.5">
                            <Trash2 className="h-4 w-4" />{t("cctv_delete_btn")}
                        </Button>
                    ) : null}
                    <Button variant="ghost" onClick={onClose}>{t("close")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
